import { Component, inject, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ImpersonationService, SecurityOverrideModel } from '../../services/impersonation.service';
import { ToastService } from '../../services/toast.service';
import { TypeaheadComponent, GroupedData, KeyValuePair } from '../../components/typeahead/typeahead.component';

import { logger } from '../../../helpers/logger';
@Component({
  selector: 'eng-impersonation-modal',
  imports: [ReactiveFormsModule, TypeaheadComponent],
  templateUrl: './impersonation.component.html',
  styleUrl: './impersonation.component.scss',
})
export class ImpersonationComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private fb = inject(FormBuilder);
  private impersonationService = inject(ImpersonationService);
  private toastService = inject(ToastService);

  impersonationForm: FormGroup;
  userTypeaheadData: GroupedData<KeyValuePair>[] = [];
  clientTypeaheadData: GroupedData<KeyValuePair>[] = [];
  selectedUserId = '';
  selectedClientId = '';
  isLoading = false;

  constructor() {
    this.impersonationForm = this.fb.group({
      userId: [''],
      clientId: [''],
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    this.isLoading = true;

    // Load users, clients, and current impersonation details in parallel
    Promise.all([
      this.impersonationService.getUserLookup().toPromise(),
      this.impersonationService.getClientLookup().toPromise(),
      this.impersonationService.getImpersonationDetails().toPromise(),
    ])
      .then(([users, clients, impersonationDetails]) => {
        if (users) {
          // Convert users to typeahead format
          const userGroups = this.impersonationService.groupUsersByClient(users);
          this.userTypeaheadData = userGroups.map(group => ({
            groupName: group.groupName,
            items: group.users.map(user => ({ key: user.id, value: user.email })),
          }));
        }
        if (clients) {
          // Convert clients to typeahead format
          this.clientTypeaheadData = [
            {
              groupName: 'Clients',
              items: clients.map(client => ({ key: client.id.toString(), value: client.name })),
            },
          ];
        }

        // Populate current impersonation details if impersonating
        if (impersonationDetails?.isImpersonating) {
          this.selectedUserId = impersonationDetails.userId || '';
          this.selectedClientId = impersonationDetails.clientId?.toString() || '';
        }

        this.isLoading = false;
      })
      .catch(error => {
        logger.error('Failed to load impersonation data:', error);
        this.toastService.error('Failed to load impersonation data');
        this.isLoading = false;
      });
  }

  onSave(): void {
    const model: SecurityOverrideModel = {};

    // Only include non-empty values
    if (this.selectedUserId && this.selectedUserId !== '') {
      model.userId = this.selectedUserId;
    }
    if (this.selectedClientId && this.selectedClientId !== '') {
      model.clientId = parseInt(this.selectedClientId, 10);
    }

    this.isLoading = true;
    this.impersonationService.startImpersonation(model).subscribe({
      next: () => {
        this.toastService.success('Impersonation started successfully');
        this.activeModal.close('saved');
        // Reload the page to reflect the impersonation changes
        window.location.href = '/';
      },
      error: error => {
        logger.error('Failed to start impersonation:', error);
        this.toastService.error('Failed to start impersonation');
        this.isLoading = false;
      },
    });
  }

  onReset(): void {
    this.isLoading = true;
    this.impersonationService.stopImpersonation().subscribe({
      next: () => {
        this.toastService.success('Impersonation stopped successfully');
        this.activeModal.close('reset');
        // Reload the page to reflect the changes
        window.location.href = '/';
      },
      error: error => {
        logger.error('Failed to stop impersonation:', error);
        this.toastService.error('Failed to stop impersonation');
        this.isLoading = false;
      },
    });
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
